//DEPENDENCIES
import { Outlet } from "react-router-dom";

//UI COMPONENTS
import AuthHeader from "../AuthHeader/AuthHeader";
import Footer from "../Footer/Footer";
import Navbar from "../NavBar/Navbar";
import MobileGenericHeader from "../MobileGenericHeader/MobileGenericHeader";
import SessionTimeoutModal from "../SessionTimeoutModal/SessionTimeoutModal";
// import AnnouncementModal from "../AnnouncementModal/AnnouncementModal";
import TermsAndConditionsModal from "../TermsAndConditionsModal/TermsAndConditionsModal";
import PopUpModal from "../PopUpModal/PopUpModal";

//STYLES
import styles from "./Layout.module.scss";
import ToastMessage from "../ToastMessage/ToastMessage";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import UnifiedBalance from "../UnifiedBalance";
import PageHeader from "../PageHeader/PageHeader";
import OperateModal from "../OperateMEP/OperateModal";
import { useIsMarketOpen } from "../../hooks/useMarketHours";
import { useEffect } from "react";
import { setMarketStatus } from "../../store/slices/marketStatusSlice";
import { fetchTransactionResources } from "../../store/slices/transactionResourcesSlice";
import CryptoSection from "../CryptoSection/CryptoSection";
import { useGtmPageView } from "../../hooks/useGtmPageView";
import { useSessionTime } from "../../hooks/useSessionTime";
import LoginGate from "../Biometrics/BiometricOptInGate";
import { useBiometricAuth } from "../../hooks/useBiometricAuth";
import { biometricsOptInPage, logout } from "../../store/slices/authSlice";
import { useSessionTimeout } from "../../hooks/useSessionTimeout";
import { isDev } from "../../utils";
import {
  openCryptoModal,
  updateCryptoStep,
} from "../../store/slices/cryptoSlice";
import { Capacitor } from "@capacitor/core";

const Layout = () => {
  useGtmPageView();
  useSessionTime();
  const {
    layoutContainer,
    contentContainer,
    outletContainer,
    toastContainer,
    toast,
  } = styles;

  const { user } = useAppSelector((state) => state.authentication);

  //Crypto
  const isCryptoOnly =
    user?.authorization?.crypto?.roles?.includes("CRYPTO_ONLY");

  const {
    isMarketOpen,
    isRepoOperationOpen,
    isHolidayToday,
    marketCanOperateT1Today,
    nextBusinessDay,
    followingBusinessDay,
  } = useIsMarketOpen();

  const { biometrics, handleBiometricAccept } = useBiometricAuth();

  const dispatch = useAppDispatch();

  const handleLogout = () => {
    dispatch(logout());
  };

  const { showModal, remainingTime, continueSession } = useSessionTimeout({
    inactivityTimeout: 28 * 60 * 1000,
    warningTimeout: 2 * 60 * 1000,
    onLogout: handleLogout,
    enabled: !(Capacitor.isNativePlatform() && biometrics.loggedIn),
  });

  useEffect(() => {
    dispatch(fetchTransactionResources());
  }, []);

  useEffect(() => {
    dispatch(
      setMarketStatus({
        isMarketOpen,
        isRepoOperationOpen,
        isHolidayToday,
        marketCanOperateT1Today,
        nextBusinessDay,
        followingBusinessDay,
      }),
    );
  }, [
    isMarketOpen,
    isRepoOperationOpen,
    isHolidayToday,
    marketCanOperateT1Today,
    dispatch,
  ]);

  useEffect(() => {
    if (isCryptoOnly && isDev) {
      dispatch(openCryptoModal());
      dispatch(updateCryptoStep("ACCOUNTS"));
    }
  }, [isCryptoOnly, dispatch]);

  const shouldShowTermsAndConditions =
    !!user &&
    !biometrics?.inProgress &&
    !biometrics?.optInPage &&
    !biometrics?.error;

  return (
    <div className={layoutContainer}>
      <ToastMessage
        messageId="layoutPage"
        position="top-right"
        className={toastContainer}
        toastClassName={toast}
      />

      <OperateModal />

      <Navbar />

      <div className={contentContainer}>
        <MobileGenericHeader />

        <div className={outletContainer}>
          <AuthHeader />
          <PageHeader />
          <Outlet />
        </div>

        <Footer />
      </div>

      {/* Unified Balance */}
      <UnifiedBalance />

      {/* CryptoSection */}
      <CryptoSection />

      {/* Login gate (biometric opt-in on native) */}
      <LoginGate
        biometrics={biometrics}
        handleBiometricAccept={handleBiometricAccept}
        onRefuse={() => {
          dispatch(biometricsOptInPage(null));
        }}
      />

      {/* Session Timeout Modal */}
      {!biometrics.loggedIn && (
        <SessionTimeoutModal
          isOpen={showModal}
          remainingTime={remainingTime}
          onContinue={continueSession}
        />
      )}

      {shouldShowTermsAndConditions && <TermsAndConditionsModal />}

      {shouldShowTermsAndConditions && <PopUpModal />}
    </div>
  );
};

export default Layout;
